import React, { useState, useRef } from 'react';
import FormField from '../FormField';
import Input from '../Input';
import PhoneInput from '../PhoneInput';
import ImageModal from '../ImageModal';
import Modal from '../Modal';
import { BusinessRoundParticipant, ThematicAxis } from '../../types/profile.types';
import { thematicAxes, subejesPorEje } from '../../data/profileData';
import { validateFileSize, MAX_FILE_SIZE_IMAGES } from '../../utils/fileValidation';
import { useFileError } from '../../contexts/FileErrorContext';
import { useUploadProgress } from '../../contexts/UploadProgressContext';
import { useTimeout } from '../../contexts/TimeoutContext';
import { uploadToSupabase } from '../../services/supabase';
import { handleRateLimitError } from '../../utils/rateLimitHandler';
import { handleUploadError } from '../../utils/uploadErrorHandler';
import CustomSelect from '../CustomSelect';
import { CheckCircleIcon, XCircleIcon } from '../icons/AdminIcons';
import './BusinessRoundsSection.css';
import './ParallelEventsSection.css';
import './ThematicAxesSection.css';
import './SpeakersSection.css';

// Helper function para obtener el estado del participante (igual que en AdminPanel)
const getBusinessRoundsStatusHelper = (participant: any): 'complete' | 'incomplete' => {
  if (!participant) return 'incomplete';
  const hasFirstName = participant.firstName && participant.firstName.trim() !== '';
  const hasLastName = participant.lastName && participant.lastName.trim() !== '';
  const hasPosition = participant.position && participant.position.trim() !== '';
  const hasOrganization = participant.organization && participant.organization.trim() !== '';
  const hasEmail = participant.email && participant.email.trim() !== '';
  const hasPhone = participant.phone && participant.phone.trim() !== '' || (participant.numeroTelefono && participant.numeroTelefono.trim() !== '');
  const hasThematicAxes = participant.thematicAxes && Array.isArray(participant.thematicAxes) && participant.thematicAxes.length > 0;
  const isComplete = hasFirstName && hasLastName && hasPosition && hasOrganization && hasEmail && hasPhone && hasThematicAxes;
  
  return isComplete ? 'complete' : 'incomplete';
};
interface BusinessRoundsSectionProps {
  participants: BusinessRoundParticipant[];
  onChange: (participants: BusinessRoundParticipant[]) => void;
  readOnly?: boolean;
  hideTitle?: boolean;
  initialExpandedParticipantId?: string;
  showApprovalBadge?: boolean;
  onApprovalStatusChange?: (participantId: string, status: 'pending' | 'approved' | 'rejected') => void;
  userId?: number;
}
interface ParticipantCardProps {
  participant: BusinessRoundParticipant;
  onUpdate: (id: string, field: keyof BusinessRoundParticipant, value: any) => void;
  onRemove: (id: string) => void;
  readOnly?: boolean;
  initialExpanded?: boolean;
  showApprovalBadge?: boolean;
  onApprovalStatusChange?: (participantId: string, status: 'pending' | 'approved' | 'rejected') => void;
  userId?: number;
}
// Función para capitalizar primera letra (igual que en InitiativesSection)
const capitalizeFirstLetter = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};
const ParticipantCard: React.FC<ParticipantCardProps> = ({ participant, onUpdate, onRemove, readOnly = false, initialExpanded = false, showApprovalBadge = false, onApprovalStatusChange, userId }) => {
  const [visibleTooltip, setVisibleTooltip] = React.useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = React.useState<{ left: number; top: number } | null>(null);
  const tooltipRef = React.useRef<HTMLDivElement | null>(null);
  const [isMobileOrTablet, setIsMobileOrTablet] = React.useState(false);
  const [emailError, setEmailError] = React.useState<string>('');

  // Validar email continuamente para que el error persista mientras haya contenido inválido
  React.useEffect(() => {
    const email = participant.email || '';
    if (email.trim() === '') {
      setEmailError('');
    } else if (!validateEmail(email)) {
      setEmailError('Por favor, ingresa un correo electrónico válido');
    } else {
      setEmailError('');
    }
  }, [participant.email]);
  
  const [organizationLogoPreview, setOrganizationLogoPreview] = React.useState<string | null>(null);
  const [showOrganizationLogoModal, setShowOrganizationLogoModal] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState<boolean>(initialExpanded);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const organizationLogoFileInputRef = React.useRef<HTMLInputElement>(null);
  const { showFileSizeError } = useFileError();
  const { startUpload, updateProgress, finishUpload } = useUploadProgress();
  const { showTimeout } = useTimeout();
  // Detectar si es mobile o tablet para tooltips
  React.useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileOrTablet(window.innerWidth <= 1024);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  // Efecto para manejar el tooltip de ejes temáticos
  React.useEffect(() => {
    if (tooltipRef.current && tooltipRef.current.parentNode) {
      tooltipRef.current.parentNode.removeChild(tooltipRef.current);
      tooltipRef.current = null;
    }
    if (visibleTooltip !== null && tooltipPosition !== null) {
      const ejeSeleccionado = thematicAxes[visibleTooltip];
      const subejes = subejesPorEje[ejeSeleccionado.label]?.subejes || [];
      if (subejes.length === 0) return;
      const isDarkMode = document.documentElement.classList.contains('dark-theme');
      const tooltipDiv = document.createElement('div');
      tooltipDiv.className = 'tooltip-content';
      tooltipDiv.style.cssText = `
        position: fixed !important;
        left: ${tooltipPosition.left}px !important;
        top: ${tooltipPosition.top}px !important;
        z-index: 999999 !important;
        background-color: ${isDarkMode ? '#0f172a' : '#ffffff'} !important;
        border: 2px solid #CE2B15 !important;
        border-radius: 8px !important;
        padding: 12px 16px !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        min-width: 280px !important;
        max-width: 400px !important;
        font-family: Montserrat, sans-serif !important;
        pointer-events: none !important;
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
      `;
      const titleDiv = document.createElement('div');
      titleDiv.style.cssText = `font-weight: 700; color: ${isDarkMode ? '#ffffff' : '#1e293b'}; font-size: 13px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid ${isDarkMode ? '#334155' : '#e5e7eb'};`;
      titleDiv.textContent = 'Incluye:';
      const ul = document.createElement('ul');
      ul.style.cssText = 'list-style: none; padding: 0; margin: 0;';
      subejes.forEach((subeje) => {
        const li = document.createElement('li');
        li.style.cssText = `font-size: 12px; color: ${isDarkMode ? '#cbd5e1' : '#64748b'}; line-height: 1.6; padding: 4px 0; padding-left: 16px; position: relative;`;
        const bullet = document.createElement('span');
        bullet.style.cssText = 'position: absolute; left: 0; color: #CE2B15; font-weight: bold;';
        bullet.textContent = '•';
        li.appendChild(bullet);
        li.appendChild(document.createTextNode(subeje));
        ul.appendChild(li);
      });
      tooltipDiv.appendChild(titleDiv);
      tooltipDiv.appendChild(ul);
      document.body.appendChild(tooltipDiv);
      tooltipRef.current = tooltipDiv;
    }
    return () => {
      if (tooltipRef.current && tooltipRef.current.parentNode) {
        tooltipRef.current.parentNode.removeChild(tooltipRef.current);
        tooltipRef.current = null;
      }
    };
  }, [visibleTooltip, tooltipPosition]);
  // Cerrar tooltip al hacer clic fuera (solo en mobile/tablet)
  React.useEffect(() => {
    if (!isMobileOrTablet || visibleTooltip === null) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        if (!target.closest('.checkbox-item')) {
          setVisibleTooltip(null);
          setTooltipPosition(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileOrTablet, visibleTooltip]);
  // Cargar preview del logo de la organización desde la base de datos
  React.useEffect(() => {
    try {
      if (participant.organizationLogo && typeof participant.organizationLogo !== 'string') {
        const reader = new FileReader();
        reader.onloadend = () => {
          setOrganizationLogoPreview(reader.result as string);
        };
        reader.onerror = () => {
          setOrganizationLogoPreview(null);
        };
        reader.readAsDataURL(participant.organizationLogo);
      } else if (typeof participant.organizationLogo === 'string' && participant.organizationLogo) {
        // Validar que sea una URL válida o base64 válido
        // Aceptar: data:image/, http://, https://, o /storage/files/ (proxy URL)
        if (participant.organizationLogo.startsWith('data:image/') || 
            participant.organizationLogo.startsWith('http://') || 
            participant.organizationLogo.startsWith('https://') ||
            participant.organizationLogo.startsWith('/storage/files/')) {
          setOrganizationLogoPreview(participant.organizationLogo);
        } else {
          setOrganizationLogoPreview(null);
        }
      } else {
        setOrganizationLogoPreview(null);
      }
    } catch (error) {
      setOrganizationLogoPreview(null);
    }
  }, [participant.organizationLogo]);
  const handleAxesChange = (value: ThematicAxis, checked: boolean) => {
    const currentAxes = participant.thematicAxes || [];
    const newAxes = checked 
      ? [...currentAxes, value]
      : currentAxes.filter(v => v !== value);
    onUpdate(participant.id, 'thematicAxes', newAxes);
  };
  // Función para capitalizar campos de texto al perder el foco (igual que en InitiativesSection)
  const handleFieldBlur = (field: 'firstName' | 'lastName' | 'position' | 'organization', value: string) => {
    const capitalized = capitalizeFirstLetter(value);
    if (capitalized !== value) {
      onUpdate(participant.id, field, capitalized);
    }
  };
  // Función para validar formato de email (igual que en InitiativesSection)
  const validateEmail = (email: string): boolean => {
    if (!email || email.trim() === '') return true; // Campo vacío es válido (se valida con required)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onUpdate(participant.id, 'email', value);
  };

  // Validar email continuamente para que el error persista mientras haya contenido inválido
  React.useEffect(() => {
    const email = participant.email || '';
    if (email.trim() === '') {
      setEmailError('');
    } else if (!validateEmail(email)) {
      setEmailError('Por favor, ingresa un correo electrónico válido');
    } else {
      setEmailError('');
    }
  }, [participant.email]);
  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    // Validar al perder el foco
    if (value && !validateEmail(value)) {
      setEmailError('Por favor, ingresa un correo electrónico válido');
    } else {
      setEmailError('');
    }
  };
  const handleCodigoPaisChange = (codigo: string) => {
    const selectedCountry = countries.find(c => c.code === codigo);
    const dialCode = selectedCountry?.dialCode || '';
    const currentNumero = participant.numeroTelefono || '';
    const phone = currentNumero.trim() ? `${dialCode} ${currentNumero.trim()}` : dialCode;
    // Actualizar ambos campos en una sola operación
    onUpdate(participant.id, 'codigoPais', codigo);
    onUpdate(participant.id, 'phone', phone);
  };
  const handleNumeroTelefonoChange = (numero: string) => {
    const currentCodigo = participant.codigoPais || 'AR';
    const selectedCountry = countries.find(c => c.code === currentCodigo) || countries[0];
    const dialCode = selectedCountry?.dialCode || '';
    const phone = numero.trim() ? `${dialCode} ${numero.trim()}` : dialCode;
    // Actualizar ambos campos
    onUpdate(participant.id, 'numeroTelefono', numero);
    onUpdate(participant.id, 'phone', phone);
  };
  const handleOrganizationLogoChange = async (file: File) => {
    // Validar tipo de archivo - solo PNG y formatos vectoriales editables (sin PDF)
    const allowedTypes = [
      'image/png',
      'image/svg+xml',
      'image/svg',
      'application/postscript',
      'application/illustrator'
    ];
    const allowedExtensions = ['.png', '.svg', '.ai', '.eps'];

    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const isValidType = allowedTypes.includes(file.type) || 
                        allowedExtensions.includes(fileExtension) ||
                        file.name.toLowerCase().endsWith('.svg') ||
                        file.name.toLowerCase().endsWith('.ai') ||
                        file.name.toLowerCase().endsWith('.eps');

    if (!isValidType) {
      showFileSizeError('El logo debe estar en formato PNG o formato de curvas editable (SVG, AI, EPS).', true);
      return;
    }

    // Validar tamaño de logo antes de agregarlo
    const validation = validateFileSize(file, MAX_FILE_SIZE_IMAGES, 'image');
    if (!validation.valid) {
      showFileSizeError(validation.error || 'El logo excede el tamaño máximo permitido.');
      return;
    }
    try {
      // Mostrar preview inmediato
      const reader = new FileReader();
      reader.onloadend = () => {
        const previewUrl = reader.result as string;
        setOrganizationLogoPreview(previewUrl);
        // Si el modal está abierto, mantenerlo abierto con la nueva imagen
        if (showOrganizationLogoModal) {
          // El modal se actualizará automáticamente porque imageUrl cambia
        }
      };
      reader.readAsDataURL(file);
      // Iniciar progreso de subida
      startUpload(file.name, 1);
      // Subir inmediatamente a Supabase
      const logoUrl = await uploadToSupabase(file, {
        folder: 'perfil-institucional/business-rounds-participants/organization-logos',
        onProgress: (progress) => {
          updateProgress(progress, 0);
        }
      });
      // Finalizar progreso
      finishUpload();
      
      // Log para debugging
      
      // Actualizar con la URL de Supabase
      onUpdate(participant.id, 'organizationLogo', logoUrl);
      
      // Forzar actualización del preview inmediatamente
      if (logoUrl) {
        setOrganizationLogoPreview(logoUrl);
      }
    } catch (error: any) {
      // Verificar si es un error de rate limiting (429)
      if (handleRateLimitError(error)) {
        finishUpload();
        return;
      }
      
      // Verificar si es un error de formato o tamaño del backend
      if (handleUploadError(error, showFileSizeError)) {
        finishUpload();
        return;
      }
      
      // Verificar si es un error de timeout
      if (error?.isTimeout) {
        showTimeout(error.operationName || 'la subida del logo de la organización');
        finishUpload();
        return;
      }
      
      // Finalizar progreso incluso si hay error
      finishUpload();
      // Error silencioso para otros tipos de error
    }
  };
  const handleOrganizationLogoChangeClick = () => {
    organizationLogoFileInputRef.current?.click();
  };
  const handleOrganizationLogoDelete = async () => {
    // Limpiar el preview
    setOrganizationLogoPreview(null);
    // Actualizar el participante sin logo
    onUpdate(participant.id, 'organizationLogo', undefined);
  };
  // Importar countries desde PhoneInput o definir localmente
  const countries = [
    { code: 'AR', name: 'Argentina', dialCode: '+54', flag: '🇦🇷' },
    { code: 'BR', name: 'Brasil', dialCode: '+55', flag: '🇧🇷' },
    { code: 'CL', name: 'Chile', dialCode: '+56', flag: '🇨🇱' },
    { code: 'CO', name: 'Colombia', dialCode: '+57', flag: '🇨🇴' },
    { code: 'MX', name: 'México', dialCode: '+52', flag: '🇲🇽' },
    { code: 'PE', name: 'Perú', dialCode: '+51', flag: '🇵🇪' },
    { code: 'UY', name: 'Uruguay', dialCode: '+598', flag: '🇺🇾' },
    { code: 'VE', name: 'Venezuela', dialCode: '+58', flag: '🇻🇪' },
    { code: 'BO', name: 'Bolivia', dialCode: '+591', flag: '🇧🇴' },
    { code: 'EC', name: 'Ecuador', dialCode: '+593', flag: '🇪🇨' },
    { code: 'PY', name: 'Paraguay', dialCode: '+595', flag: '🇵🇾' },
    { code: 'CR', name: 'Costa Rica', dialCode: '+506', flag: '🇨🇷' },
    { code: 'PA', name: 'Panamá', dialCode: '+507', flag: '🇵🇦' },
    { code: 'GT', name: 'Guatemala', dialCode: '+502', flag: '🇬🇹' },
    { code: 'HN', name: 'Honduras', dialCode: '+504', flag: '🇭🇳' },
    { code: 'SV', name: 'El Salvador', dialCode: '+503', flag: '🇸🇻' },
    { code: 'NI', name: 'Nicaragua', dialCode: '+505', flag: '🇳🇮' },
    { code: 'CU', name: 'Cuba', dialCode: '+53', flag: '🇨🇺' },
    { code: 'DO', name: 'República Dominicana', dialCode: '+1', flag: '🇩🇴' },
    { code: 'HT', name: 'Haití', dialCode: '+509', flag: '🇭🇹' },
    { code: 'JM', name: 'Jamaica', dialCode: '+1', flag: '🇯🇲' },
    { code: 'TT', name: 'Trinidad y Tobago', dialCode: '+1', flag: '🇹🇹' },
    { code: 'US', name: 'Estados Unidos', dialCode: '+1', flag: '🇺🇸' },
    { code: 'CA', name: 'Canadá', dialCode: '+1', flag: '🇨🇦' },
    { code: 'ES', name: 'España', dialCode: '+34', flag: '🇪🇸' },
    { code: 'IT', name: 'Italia', dialCode: '+39', flag: '🇮🇹' },
    { code: 'FR', name: 'Francia', dialCode: '+33', flag: '🇫🇷' },
    { code: 'DE', name: 'Alemania', dialCode: '+49', flag: '🇩🇪' },
    { code: 'GB', name: 'Reino Unido', dialCode: '+44', flag: '🇬🇧' },
    { code: 'PT', name: 'Portugal', dialCode: '+351', flag: '🇵🇹' },
    { code: 'NL', name: 'Países Bajos', dialCode: '+31', flag: '🇳🇱' },
    { code: 'BE', name: 'Bélgica', dialCode: '+32', flag: '🇧🇪' },
    { code: 'CH', name: 'Suiza', dialCode: '+41', flag: '🇨🇭' },
    { code: 'AT', name: 'Austria', dialCode: '+43', flag: '🇦🇹' },
    { code: 'SE', name: 'Suecia', dialCode: '+46', flag: '🇸🇪' },
    { code: 'NO', name: 'Noruega', dialCode: '+47', flag: '🇳🇴' },
    { code: 'DK', name: 'Dinamarca', dialCode: '+45', flag: '🇩🇰' },
    { code: 'FI', name: 'Finlandia', dialCode: '+358', flag: '🇫🇮' },
    { code: 'PL', name: 'Polonia', dialCode: '+48', flag: '🇵🇱' },
    { code: 'CZ', name: 'República Checa', dialCode: '+420', flag: '🇨🇿' },
    { code: 'HU', name: 'Hungría', dialCode: '+36', flag: '🇭🇺' },
    { code: 'RO', name: 'Rumania', dialCode: '+40', flag: '🇷🇴' },
    { code: 'BG', name: 'Bulgaria', dialCode: '+359', flag: '🇧🇬' },
    { code: 'GR', name: 'Grecia', dialCode: '+30', flag: '🇬🇷' },
    { code: 'RU', name: 'Rusia', dialCode: '+7', flag: '🇷🇺' },
    { code: 'UA', name: 'Ucrania', dialCode: '+380', flag: '🇺🇦' },
    { code: 'IE', name: 'Irlanda', dialCode: '+353', flag: '🇮🇪' },
    { code: 'IS', name: 'Islandia', dialCode: '+354', flag: '🇮🇸' },
    { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' },
    { code: 'JP', name: 'Japón', dialCode: '+81', flag: '🇯🇵' },
    { code: 'KR', name: 'Corea del Sur', dialCode: '+82', flag: '🇰🇷' },
    { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
    { code: 'TH', name: 'Tailandia', dialCode: '+66', flag: '🇹🇭' },
    { code: 'SG', name: 'Singapur', dialCode: '+65', flag: '🇸🇬' },
    { code: 'MY', name: 'Malasia', dialCode: '+60', flag: '🇲🇾' },
    { code: 'ID', name: 'Indonesia', dialCode: '+62', flag: '🇮🇩' },
    { code: 'PH', name: 'Filipinas', dialCode: '+63', flag: '🇵🇭' },
    { code: 'VN', name: 'Vietnam', dialCode: '+84', flag: '🇻🇳' },
    { code: 'TW', name: 'Taiwán', dialCode: '+886', flag: '🇹🇼' },
    { code: 'HK', name: 'Hong Kong', dialCode: '+852', flag: '🇭🇰' },
    { code: 'BD', name: 'Bangladesh', dialCode: '+880', flag: '🇧🇩' },
    { code: 'PK', name: 'Pakistán', dialCode: '+92', flag: '🇵🇰' },
    { code: 'LK', name: 'Sri Lanka', dialCode: '+94', flag: '🇱🇰' },
    { code: 'NP', name: 'Nepal', dialCode: '+977', flag: '🇳🇵' },
    { code: 'MM', name: 'Myanmar', dialCode: '+95', flag: '🇲🇲' },
    { code: 'KH', name: 'Camboya', dialCode: '+855', flag: '🇰🇭' },
    { code: 'LA', name: 'Laos', dialCode: '+856', flag: '🇱🇦' },
    { code: 'MN', name: 'Mongolia', dialCode: '+976', flag: '🇲🇳' },
    { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
    { code: 'NZ', name: 'Nueva Zelanda', dialCode: '+64', flag: '🇳🇿' },
    { code: 'FJ', name: 'Fiyi', dialCode: '+679', flag: '🇫🇯' },
    { code: 'PG', name: 'Papúa Nueva Guinea', dialCode: '+675', flag: '🇵🇬' },
    { code: 'NC', name: 'Nueva Caledonia', dialCode: '+687', flag: '🇳🇨' },
    { code: 'ZA', name: 'Sudáfrica', dialCode: '+27', flag: '🇿🇦' },
    { code: 'EG', name: 'Egipto', dialCode: '+20', flag: '🇪🇬' },
    { code: 'NG', name: 'Nigeria', dialCode: '+234', flag: '🇳🇬' },
    { code: 'KE', name: 'Kenia', dialCode: '+254', flag: '🇰🇪' },
    { code: 'MA', name: 'Marruecos', dialCode: '+212', flag: '🇲🇦' },
    { code: 'TN', name: 'Túnez', dialCode: '+216', flag: '🇹🇳' },
    { code: 'DZ', name: 'Argelia', dialCode: '+213', flag: '🇩🇿' },
    { code: 'GH', name: 'Ghana', dialCode: '+233', flag: '🇬🇭' },
    { code: 'ET', name: 'Etiopía', dialCode: '+251', flag: '🇪🇹' },
    { code: 'UG', name: 'Uganda', dialCode: '+256', flag: '🇺🇬' },
    { code: 'TZ', name: 'Tanzania', dialCode: '+255', flag: '🇹🇿' },
    { code: 'ZW', name: 'Zimbabue', dialCode: '+263', flag: '🇿🇼' },
    { code: 'BW', name: 'Botsuana', dialCode: '+267', flag: '🇧🇼' },
    { code: 'NA', name: 'Namibia', dialCode: '+264', flag: '🇳🇦' },
    { code: 'ZM', name: 'Zambia', dialCode: '+260', flag: '🇿🇲' },
    { code: 'MW', name: 'Malaui', dialCode: '+265', flag: '🇲🇼' },
    { code: 'MZ', name: 'Mozambique', dialCode: '+258', flag: '🇲🇿' },
    { code: 'MG', name: 'Madagascar', dialCode: '+261', flag: '🇲🇬' },
    { code: 'MU', name: 'Mauricio', dialCode: '+230', flag: '🇲🇺' },
    { code: 'SC', name: 'Seychelles', dialCode: '+248', flag: '🇸🇨' },
    { code: 'SA', name: 'Arabia Saudí', dialCode: '+966', flag: '🇸🇦' },
    { code: 'AE', name: 'Emiratos Árabes Unidos', dialCode: '+971', flag: '🇦🇪' },
    { code: 'QA', name: 'Catar', dialCode: '+974', flag: '🇶🇦' },
    { code: 'KW', name: 'Kuwait', dialCode: '+965', flag: '🇰🇼' },
    { code: 'BH', name: 'Baréin', dialCode: '+973', flag: '🇧🇭' },
    { code: 'OM', name: 'Omán', dialCode: '+968', flag: '🇴🇲' },
    { code: 'JO', name: 'Jordania', dialCode: '+962', flag: '🇯🇴' },
    { code: 'LB', name: 'Líbano', dialCode: '+961', flag: '🇱🇧' },
    { code: 'SY', name: 'Siria', dialCode: '+963', flag: '🇸🇾' },
    { code: 'IQ', name: 'Irak', dialCode: '+964', flag: '🇮🇶' },
    { code: 'IR', name: 'Irán', dialCode: '+98', flag: '🇮🇷' },
    { code: 'IL', name: 'Israel', dialCode: '+972', flag: '🇮🇱' },
    { code: 'TR', name: 'Turquía', dialCode: '+90', flag: '🇹🇷' }
  ];
  return (
    <div className="parallel-event-card participant-card" style={{ position: 'relative', overflow: 'visible' }}>
      {/* Badges de estado en el borde superior derecho (solo en readonly del popup) */}
      {readOnly && showApprovalBadge && (() => {
        const status = getBusinessRoundsStatusHelper(participant);
        // Para business rounds, solo mostrar selector si está completo
        const shouldShowApprovalSelector = status === 'complete';
        
        return (
          <div className="admin-participation-card-status-badges" style={{ marginRight: '10px' }}>
            {/* Badge de estado de completitud - siempre se muestra si corresponde */}
            <span className={`participation-status-badge status-${status}`}>
              {status === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}
              {status === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}
            </span>
            {/* Selector de estado de aprobación - solo se muestra si está completo */}
            {shouldShowApprovalSelector && onApprovalStatusChange && (
              <div style={{ minWidth: '120px', maxWidth: '140px', flexShrink: 0 }}>
                <CustomSelect
                  className="admin-filter-select admin-filter-select-small"
                  value={participant.approval_status || 'pending'}
                  onChange={(newStatus) => {
                    if (onApprovalStatusChange) {
                      onApprovalStatusChange(participant.id, newStatus as 'pending' | 'approved' | 'rejected');
                    }
                  }}
                  options={[
                    { value: 'pending', label: 'Pendiente', color: '#3b82f6' },
                    { value: 'approved', label: 'Aprobado', color: '#10b981' },
                    { value: 'rejected', label: 'Rechazado', color: '#ef4444' }
                  ]}
                />
              </div>
            )}
          </div>
        );
      })()}
      <div className="event-header">
        <h3>
          {participant.firstName || participant.lastName
            ? `${participant.firstName || ''} ${participant.lastName || ''}`.trim()
            : 'Nuevo participante'}
        </h3>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            type="button"
            className="minimize-button"
            onClick={() => setIsExpanded(!isExpanded)}
            title={isExpanded ? 'Minimizar' : 'Expandir'}
            style={{
              background: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              padding: '6px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748b',
              fontSize: '14px',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 500
            }}
          >
            {isExpanded ? '−' : '+'}
          </button>
          {!readOnly && (
            <button
              type="button"
              className="remove-button"
              onClick={() => setShowDeleteModal(true)}
            >
              Eliminar
            </button>
          )}
        </div>
      </div>
      {isExpanded && (
      <div className="parallel-event-card-content">
        <FormField label="Logo de la organización">
          <div className="photo-upload-container">
            <div className="photo-preview">
              {organizationLogoPreview ? (
                <img 
                  src={organizationLogoPreview} 
                  alt="Logo de la organización" 
                  className="photo-preview-image"
                  onClick={() => setShowOrganizationLogoModal(true)}
                  style={{ cursor: 'pointer' }}
                />
              ) : (
                <div className="photo-placeholder">
                  <p>Sin logo</p>
                </div>
              )}
            </div>
            {organizationLogoPreview && (
              <ImageModal
                isOpen={showOrganizationLogoModal}
                imageUrl={organizationLogoPreview}
                alt="Logo de la organización"
                onClose={() => setShowOrganizationLogoModal(false)}
                onSave={async (editedImageUrl) => {
                  // Convertir la URL del blob a File
                  try {
                    // La URL puede ser blob, data, o una URL de WordPress
                    // Si es una URL de WordPress, necesitamos convertirla a blob primero
                    if (!editedImageUrl) {
                      return;
                    }
                    let blob: Blob;
                    if (editedImageUrl.startsWith('blob:') || editedImageUrl.startsWith('data:')) {
                      // Es un blob o data URL, hacer fetch directamente
                      const response = await fetch(editedImageUrl);
                      if (!response.ok) {
                        throw new Error(`Error al cargar la imagen: ${response.status} ${response.statusText}`);
                      }
                      blob = await response.blob();
                    } else {
                      // Es una URL de WordPress, usar el proxy para evitar CORS
                      let proxyBaseUrl = process.env.REACT_APP_API_URL || window.location.origin;
                      proxyBaseUrl = proxyBaseUrl.replace(/\/api\/?$/, '');
                      const proxyUrl = `${proxyBaseUrl}/api/proxy-image?url=${encodeURIComponent(editedImageUrl)}`;
                      const response = await fetch(proxyUrl);
                      if (!response.ok) {
                        throw new Error(`Error al cargar la imagen: ${response.status} ${response.statusText}`);
                      }
                      blob = await response.blob();
                    }
                    // Extraer el nombre original del archivo de la URL original
                    const getOriginalFileName = (url: string): string => {
                      try {
                        // Si es una URL de WordPress, extraer el nombre del archivo
                        if (url && url.includes('/')) {
                          const urlParts = url.split('/');
                          const fileName = urlParts[urlParts.length - 1];
                          // Remover parámetros de query si existen
                          const fileNameWithoutParams = fileName.split('?')[0];
                          // Si tiene nombre válido, usarlo
                          if (fileNameWithoutParams && fileNameWithoutParams.includes('.')) {
                            return fileNameWithoutParams;
                          }
                        }
                        // Si es un blob o data URL, usar nombre por defecto
                        return 'logo.png';
                      } catch {
                        return 'logo.png';
                      }
                    };
                    const originalFileName = getOriginalFileName(organizationLogoPreview || '');
                    // Extraer nombre y extensión
                    const lastDotIndex = originalFileName.lastIndexOf('.');
                    let fileNameWithoutExt = originalFileName;
                    let fileExtension = '.png';
                    if (lastDotIndex > 0) {
                      fileNameWithoutExt = originalFileName.substring(0, lastDotIndex);
                      fileExtension = originalFileName.substring(lastDotIndex);
                    }
                    // Agregar "-edited" antes de la extensión
                    const editedFileName = `${fileNameWithoutExt}-edited${fileExtension}`;
                    const file = new File([blob], editedFileName, { type: blob.type || 'image/png' });
                    // Actualizar el preview
                    setOrganizationLogoPreview(editedImageUrl);
                    // Subir la imagen editada
                    await handleOrganizationLogoChange(file);
                  } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
                    // Error silencioso - no mostrar diálogo al usuario
                  }
                }}
                allowEdit={!readOnly}
                isPhoto={true}
                onPhotoChange={handleOrganizationLogoChangeClick}
                onPhotoDelete={handleOrganizationLogoDelete}
              />
            )}
            <input
              ref={organizationLogoFileInputRef}
              type="file"
              accept="image/png,image/svg+xml,image/svg,.svg,.ai,.eps,application/postscript,application/illustrator"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleOrganizationLogoChange(file);
                }
                // Resetear el input
                if (e.target) {
                  e.target.value = '';
                }
              }}
              style={{ display: 'none' }}
            />
            {!readOnly && (
              <button 
                className="photo-upload-btn"
                onClick={() => {
                  if (organizationLogoPreview) {
                    setShowOrganizationLogoModal(true);
                  } else {
                    organizationLogoFileInputRef.current?.click();
                  }
                }}
              >
                {organizationLogoPreview ? 'Editar Logo' : 'Subir Logo'}
              </button>
            )}
            <p style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', fontStyle: 'italic' }}>
              En caso de que este campo quede vacío, se usará el logo de tu organización para las comunicaciones.
            </p>
          </div>
        </FormField>
        <div className="form-row">
          <FormField label="Nombre" required>
            <Input
              value={participant.firstName || ''}
              onChange={(e) => onUpdate(participant.id, 'firstName', e.target.value)}
              onBlur={(e) => handleFieldBlur('firstName', e.target.value)}
              disabled={readOnly}
              placeholder="Nombre"
            />
          </FormField>
          <FormField label="Apellido" required>
            <Input
              value={participant.lastName || ''}
              onChange={(e) => onUpdate(participant.id, 'lastName', e.target.value)}
              onBlur={(e) => handleFieldBlur('lastName', e.target.value)}
              disabled={readOnly}
              placeholder="Apellido"
            />
          </FormField>
        </div>
        <div className="form-row">
          <FormField label="Cargo" required>
            <Input
              value={participant.position || ''}
              onChange={(e) => onUpdate(participant.id, 'position', e.target.value)}
              onBlur={(e) => handleFieldBlur('position', e.target.value)}
              disabled={readOnly}
              placeholder="Cargo"
            />
          </FormField>
          <FormField label="Organización" required>
            <Input
              value={participant.organization || ''}
              onChange={(e) => onUpdate(participant.id, 'organization', e.target.value)}
              onBlur={(e) => handleFieldBlur('organization', e.target.value)}
              disabled={readOnly}
              placeholder="Organización"
            />
          </FormField>
        </div>
        <div className="form-row">
          <FormField label="Correo" required error={emailError}>
            <Input
              type="email"
              value={participant.email || ''}
              onChange={handleEmailChange}
              onBlur={handleEmailBlur}
              disabled={readOnly}
              placeholder="correo@ejemplo.com"
              error={!!emailError}
            />
          </FormField>
          <FormField label="Teléfono" required>
            <PhoneInput
              codigoPais={participant.codigoPais || 'AR'}
              numeroTelefono={participant.numeroTelefono || ''}
              onCodigoPaisChange={handleCodigoPaisChange}
              onNumeroTelefonoChange={handleNumeroTelefonoChange}
              disabled={readOnly}
            />
          </FormField>
        </div>
        <FormField 
          label="Ejes de interés" 
          required
          helpText={
            <>
              Selecciona uno o varios ejes temáticos (Conoce más de cada eje en{' '}
              <a 
                href="https://smartcityexposantiagodelestero.com/tematicas" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  color: '#CE2B15', 
                  textDecoration: 'none',
                  fontWeight: 600
                }}
                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
              >
                Temáticas
              </a>
              )
            </>
          }
        >
          <div className="checkbox-group thematic-axes-two-columns">
            {/* Selector "Seleccionar todos" */}
            <label 
              className="checkbox-item select-all-checkbox"
              style={{
                fontWeight: 600,
                marginBottom: '8px',
                width: '100%'
              }}
            >
              <input
                type="checkbox"
                checked={
                  (participant.thematicAxes || []).length > 0 &&
                  (participant.thematicAxes || []).length === thematicAxes.length
                }
                onChange={(e) => {
                  if (readOnly) return;
                  if (e.target.checked) {
                    // Seleccionar todos
                    const allAxes = thematicAxes.map(axis => axis.value);
                    onUpdate(participant.id, 'thematicAxes', allAxes);
                  } else {
                    // Deseleccionar todos
                    onUpdate(participant.id, 'thematicAxes', []);
                  }
                }}
                disabled={readOnly}
              />
              <span className="checkbox-label">Seleccionar todos</span>
            </label>
            {thematicAxes.map((option, index) => {
              const subejes = subejesPorEje[option.label]?.subejes || [];
              const hasSubejes = subejes.length > 0;
              return (
                <label 
                  key={option.value} 
                  className={`checkbox-item ${hasSubejes ? 'has-tooltip' : ''}`}
                  onMouseEnter={(e) => {
                    if (hasSubejes && !isMobileOrTablet) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const tooltipWidth = 300;
                      const spacing = 10;
                      let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                      let top = rect.bottom + spacing;
                      if (left + tooltipWidth > window.innerWidth) {
                        left = window.innerWidth - tooltipWidth - 10;
                      }
                      if (left < 10) {
                        left = 10;
                      }
                      const tooltipHeight = 200;
                      if (top + tooltipHeight > window.innerHeight) {
                        top = rect.top - tooltipHeight - spacing;
                      }
                      if (top < 10) {
                        top = 10;
                      }
                      setVisibleTooltip(index);
                      setTooltipPosition({ left, top });
                    }
                  }}
                  onMouseLeave={() => {
                    if (!isMobileOrTablet) {
                      setVisibleTooltip(null);
                      setTooltipPosition(null);
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={(participant.thematicAxes || []).includes(option.value)}
                    onChange={(e) => {
                      if (readOnly) return;
                      handleAxesChange(option.value, e.target.checked);
                    }}
                    disabled={readOnly}
                  />
                  <span className="checkbox-label">{option.label}</span>
                  {hasSubejes && isMobileOrTablet && (
                    <span
                      className="modalidad-info-icon"
                      style={{
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: '#f3f4f6',
                        color: '#6b7280',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        border: '1px solid #d1d5db',
                        flexShrink: 0,
                        transition: 'all 0.2s ease',
                        marginLeft: '8px',
                        display: 'flex'
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setVisibleTooltip(visibleTooltip === index ? null : index);
                        setTooltipPosition({ left: rect.left, top: rect.bottom + 5 });
                      }}
                    >
                      ?
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </FormField>
        
        {/* Badges de estado al final (solo en readonly del popup) */}
        {readOnly && showApprovalBadge && (() => {
          const status = getBusinessRoundsStatusHelper(participant);
          // Para business rounds, solo mostrar selector si está completo
          const shouldShowApprovalSelector = status === 'complete';
          
          return (
            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap' }}>
                {/* Badge de estado de completitud - siempre se muestra si corresponde */}
                <span className={`participation-status-badge status-${status}`}>
                  {status === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}
                  {status === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}
                </span>
                {/* Selector de estado de aprobación - solo se muestra si está completo */}
                {shouldShowApprovalSelector && onApprovalStatusChange && (
                  <div style={{ minWidth: '120px', maxWidth: '140px', flexShrink: 0 }}>
                    <CustomSelect
                      className="admin-filter-select admin-filter-select-small"
                      value={participant.approval_status || 'pending'}
                      onChange={(newStatus) => {
                        if (onApprovalStatusChange) {
                          onApprovalStatusChange(participant.id, newStatus as 'pending' | 'approved' | 'rejected');
                        }
                      }}
                      options={[
                        { value: 'pending', label: 'Pendiente', color: '#3b82f6' },
                        { value: 'approved', label: 'Aprobado', color: '#10b981' },
                        { value: 'rejected', label: 'Rechazado', color: '#ef4444' }
                      ]}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>
      )}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
        }}
        onConfirm={() => {
          onRemove(participant.id);
          setShowDeleteModal(false);
        }}
        confirmText="Aceptar"
        cancelText="Cancelar"
      >
        <p>¿Estás seguro de que deseas eliminar este participante?</p>
      </Modal>
    </div>
  );
};
const BusinessRoundsSection: React.FC<BusinessRoundsSectionProps> = ({
  participants: participantsProp,
  onChange,
  readOnly = false,
  hideTitle = false,
  initialExpandedParticipantId,
  showApprovalBadge = false,
  onApprovalStatusChange,
  userId
}) => {
  const [participants, setParticipants] = useState<BusinessRoundParticipant[]>(participantsProp || []);
  const [newParticipantIds, setNewParticipantIds] = React.useState<Set<string>>(new Set());
  // Sincronizar con cambios externos
  React.useEffect(() => {
    setParticipants(participantsProp || []);
  }, [participantsProp]);
  const addParticipant = () => {
    const newId = `participant_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setNewParticipantIds(prev => new Set(prev).add(newId));
    const newParticipant: BusinessRoundParticipant = {
      id: newId,
      firstName: '',
      lastName: '',
      position: '',
      organization: '',
      email: '',
      phone: '',
      codigoPais: 'AR',
      numeroTelefono: '',
      thematicAxes: []
    };
    const updatedParticipants = [...participants, newParticipant];
    setParticipants(updatedParticipants);
    onChange(updatedParticipants);
  };
  const updateParticipant = React.useCallback((id: string, field: keyof BusinessRoundParticipant, value: any) => {
    setParticipants(prevParticipants => {
      const updatedParticipants = prevParticipants.map(p => 
        p.id === id ? { ...p, [field]: value } : p
      );
      onChange(updatedParticipants);
      return updatedParticipants;
    });
  }, [onChange]);
  const updateParticipantMultiple = (id: string, updates: Partial<BusinessRoundParticipant>) => {
    const updatedParticipants = participants.map(p => 
      p.id === id ? { ...p, ...updates } : p
    );
    setParticipants(updatedParticipants);
    onChange(updatedParticipants);
  };
  const removeParticipant = (id: string) => {
    const updatedParticipants = participants.filter(p => p.id !== id);
    setParticipants(updatedParticipants);
    onChange(updatedParticipants);
  };
  return (
    <>
      {!hideTitle && (
        <div style={{ marginTop: '60px', marginBottom: '20px' }}>
          <h3 className="initiatives-subsection-title" style={{ 
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '20px',
            fontWeight: 700,
            color: '#1e293b',
            marginBottom: '8px',
            marginTop: 0
          }}>
            Rondas de negocios
          </h3>
          <p style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '14px',
            color: '#64748b',
            marginTop: '8px',
            marginBottom: 0
          }}>
            Datos de quienes participaran en las rondas de negocios
          </p>
        </div>
      )}
      <div className="initiative-section-divider" style={{ marginTop: hideTitle ? '0' : '20px', paddingTop: hideTitle ? '0' : '30px', borderTop: hideTitle ? 'none' : '2px solid #e5e7eb' }}>
      {participants.map((participant) => (
        <ParticipantCard
          key={participant.id}
          participant={participant}
          onUpdate={updateParticipant}
          onRemove={removeParticipant}
          readOnly={readOnly}
          initialExpanded={newParticipantIds.has(participant.id) || (initialExpandedParticipantId !== undefined && participant.id === initialExpandedParticipantId)}
          showApprovalBadge={showApprovalBadge}
          onApprovalStatusChange={onApprovalStatusChange}
          userId={userId}
        />
      ))}
      {!readOnly && (
        <button
          type="button"
          className="add-button"
          onClick={addParticipant}
          style={{
            marginTop: '20px'
          }}
        >
          + Agregar Participante
        </button>
      )}
      </div>
    </>
  );
};
export default BusinessRoundsSection;
